import {
  ChevronForward,
  ShieldCheckmarkOutline,
  Star,
} from "@raresail/react-ionicons";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { TListHook } from "../../@types/common";
import { INSTITUTIONS } from "../../constants/constants";
import { InstitutionsData } from "../../dump";
import { formatLargeNumber, stringify } from "../../utils/helpers";
import { searchFilterField } from "../../constants/options";
import {
  primary5,
  PRIMARY_COLOR,
  PRIMARY_LIGHT,
} from "../../constants/colors";
import { alpha } from "@mui/material";

export const useInstitutionsList = (): TListHook => {
  const filters = [searchFilterField];

  return {
    ListItem,
    title: "Marketplace",
    subtitle: "Explore verified Ugandan unit trust funds",
    defaultValues: InstitutionsData,
    filters,
  };
};

const ListItem = ({ data }: any) => {
  const navigate = useNavigate();

  const {
    id,
    name,
    type,
    image,
    rating,
    total_funds_managed,
    products = [],
  } = data || {};

  const bestRate = Math.max(...products.map((p: any) => p.annual_rate || 0));

  return (
    <Card
      className="whiteBg r15 hidden pointer animate"
      onClick={() => navigate(`/institutions/${id}`)}
    >
      {/* Image + overlay info */}
      <ImageSection>
        <img src={image} alt={name} />
        <ImageOverlay />
        {/* Rating badge */}
        {rating && (
          <RatingBadge>
            <Star fontSize={11} color="#f5a623" />
            {rating}
          </RatingBadge>
        )}
        {/* Name on image */}
        <NameOverlay>
          <div className="font16 bold white">{name}</div>
          <div className="font11 white" style={{ opacity: 0.85 }}>{type}</div>
        </NameOverlay>
      </ImageSection>

      {/* Body — compact */}
      <Body>
        {/* Quick stats inline */}
        <StatsRow>
          <div className="flexNullCenter gap5">
            <ShieldCheckmarkOutline fontSize={12} color="#2e7d32" />
            <span className="font11" style={{ color: "#2e7d32" }}>CMA Licensed</span>
          </div>
          <span className="font11 grey">
            UGX {formatLargeNumber(total_funds_managed)}
          </span>
        </StatsRow>

        {/* Products + rates */}
        <div className="flexColumn" style={{ gap: 8 }}>
          {products.map((p: any, i: number) => (
            <ProductRow key={i}>
              <span className="font12 textDark">{p.name}</span>
              <span className="font12 semiBold" style={{ color: "#2e7d32" }}>
                {p.annual_rate}% p.a.
              </span>
            </ProductRow>
          ))}
        </div>

        {/* CTA */}
        <CTARow>
          <span className="font12 semiBold">Learn more</span>
          <ChevronForward fontSize={14} />
        </CTARow>
      </Body>
    </Card>
  );
};

const Card = styled.div`
  border: 1px solid ${PRIMARY_LIGHT};

  &:hover {
    border-color: ${alpha(PRIMARY_COLOR, 0.2)};
    box-shadow: 0 4px 20px ${alpha(PRIMARY_COLOR, 0.06)};
  }
`;

const ImageSection = styled.div`
  position: relative;
  height: 140px;
  overflow: hidden;
  background: #f0f2f5;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const ImageOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 70%;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.55));
`;

const RatingBadge = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  display: flex;
  align-items: center;
  gap: 3px;
  font-size: 12px;
  font-weight: 600;
  color: #333;
  background: rgba(255, 255, 255, 0.92);
  padding: 4px 9px;
  border-radius: 20px;
`;

const NameOverlay = styled.div`
  position: absolute;
  bottom: 10px;
  left: 14px;
`;

const Body = styled.div`
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const StatsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ProductRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 10px 14px;
  background: ${primary5};
  border-radius: 10px;
`;

const CTARow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 8px;
  color: ${PRIMARY_COLOR};
`;